import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { mobileBootstrap } from './lib/mobileBootstrap';
import App from './App';
import './index.css';

/**
 * C16-MOBILE: await native + auth bootstrap once before React mounts.
 *
 * mobileBootstrap is built so it ALWAYS resolves (every internal step is
 * try/catch wrapped, the auth refresh has a 5s timeout, and the finally
 * block flips _hasHydrated regardless). That means we can `await` it
 * safely instead of using `.finally()` — the previous pattern was a defense
 * against a possible reject, which is no longer reachable.
 *
 * On web this resolves in microtasks-to-~300ms (refresh round trip); on
 * native it also configures status bar, back button, splash hide and logs
 * device info in dev. Failures inside bootstrap are swallowed so we never
 * block render on a broken plugin.
 *
 * By the time createRoot().render() runs, `useAuthStore._hasHydrated` is
 * `true` and accessToken is either valid (refresh succeeded) or null
 * (refresh failed → router will redirect to /login). DashboardPage and
 * other gated components can safely consult `_hasHydrated` to know boot is
 * done.
 */
async function bootstrapAndRender(): Promise<void> {
  await mobileBootstrap();
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <BrowserRouter>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

// Top-level await is not enabled in this Vite config; wrap and swallow.
// `void` keeps the no-floating-promises lint rule happy without forcing the
// caller to handle a promise that intentionally never rejects.
void bootstrapAndRender();

// El service worker (VitePWA autoUpdate + skipWaiting + clientsClaim) toma
// control de la página en cuanto se instala una versión nueva, pero el JS ya
// cargado en memoria sigue siendo el viejo hasta el siguiente reload — sin
// esto, un usuario puede quedarse sesiones enteras en un bundle desactualizado
// (o peor: mitad viejo, mitad nuevo si navega y el chunk viejo ya no existe).
// El guard de `hadController` evita recargar en la PRIMERA instalación
// (clientsClaim también dispara controllerchange ahí). En Capacitor no hay SW
// registrado, así que el listener nunca dispara.
if ('serviceWorker' in navigator) {
  let hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    window.location.reload();
  });
}
